import type { CaravanDb } from './db'
import { LocalRepository } from './repository'
import type { ConflictHistory, LocalEntity } from '../../domain/sync'

export type AgendaEvent = {
  id: string
  tripId: string
  date: string
  time: string
  title: string
  place: string
  version: number
  syncState: LocalEntity['syncState']
  updatedAtServer: string | null
}

export type AgendaEventInput = Pick<AgendaEvent, 'tripId' | 'date' | 'time' | 'title' | 'place'>

function fromEntity(entity: LocalEntity): AgendaEvent {
  return {
    id: entity.id,
    tripId: entity.tripId,
    date: typeof entity.payload.date === 'string' ? entity.payload.date : '',
    time: typeof entity.payload.time === 'string' ? entity.payload.time : '',
    title: typeof entity.payload.title === 'string' ? entity.payload.title : '',
    place: typeof entity.payload.place === 'string' ? entity.payload.place : '',
    version: entity.version,
    syncState: entity.syncState,
    updatedAtServer: entity.updatedAtServer,
  }
}

export class AgendaRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
  }

  async create(input: AgendaEventInput): Promise<AgendaEvent> {
    const id = crypto.randomUUID()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'event',
      entityId: id,
      tripId: input.tripId,
      operation: 'create',
      baseVersion: 0,
      payload: { date: input.date, time: input.time, title: input.title, place: input.place },
      createdAtDevice: new Date().toISOString(),
    })
    return fromEntity(entity)
  }

  async update(id: string, input: AgendaEventInput, baseVersion: number): Promise<AgendaEvent> {
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'event',
      entityId: id,
      tripId: input.tripId,
      operation: 'update',
      baseVersion,
      payload: { date: input.date, time: input.time, title: input.title, place: input.place },
      createdAtDevice: new Date().toISOString(),
    })
    return fromEntity(entity)
  }

  async delete(id: string, tripId: string, baseVersion: number): Promise<void> {
    await this.#local.mutate({
      mutationId: crypto.randomUUID(), entityType: 'event', entityId: id, tripId,
      operation: 'delete', baseVersion, payload: {}, createdAtDevice: new Date().toISOString(),
    })
  }

  async list(): Promise<AgendaEvent[]> {
    const entities = await this.#db.entities
      .where('entityType')
      .equals('event')
      .filter((entity) => entity.deletedAt === null)
      .toArray()
    return entities.map(fromEntity).sort((left, right) =>
      `${left.date}T${left.time}`.localeCompare(`${right.date}T${right.time}`),
    )
  }

  async get(id: string): Promise<AgendaEvent | null> {
    const entity = await this.#db.entities.get(`event:${id}`)
    return entity?.deletedAt === null ? fromEntity(entity) : null
  }

  async applyServerWinner(
    id: string,
    winner: {
      mutationId: string
      receivedAtServer: string
      version: number
      payload: Record<string, unknown>
    },
  ): Promise<void> {
    await this.#db.transaction('rw', this.#db.entities, this.#db.histories, async () => {
      const entity = await this.#db.entities.get(`event:${id}`)
      if (!entity) throw new Error('Événement local introuvable.')
      const history: ConflictHistory = {
        id: `event:${id}:${winner.mutationId}`,
        entityType: 'event',
        entityId: id,
        localPayload: structuredClone(entity.payload),
        serverPayload: structuredClone(winner.payload),
        localVersion: entity.version,
        serverVersion: winner.version,
        recordedAt: winner.receivedAtServer,
        replacingMutationId: winner.mutationId,
      }
      await this.#db.histories.put(history)
      await this.#db.entities.update(entity.key, {
        payload: structuredClone(winner.payload),
        version: winner.version,
        updatedAtServer: winner.receivedAtServer,
        lastMutationId: winner.mutationId,
        syncState: 'synced',
        lastError: null,
      })
    })
  }
}
